import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Menu, X, ChevronDown } from 'lucide-react';
import logo from '@/assets/logo.png';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useHeaderConfig, localizedLabel, type HeaderItem, type HeaderCategory } from '@/hooks/useHeaderConfig';

export const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, signOut, isStaff } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openMobileCats, setOpenMobileCats] = useState<Record<string, boolean>>({});
  const { config } = useHeaderConfig();

  const initials = (user?.user_metadata?.full_name || user?.email || '?')
    .split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  // Fetch avatar and unread count in a single effect
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        // Fetch avatar and conversations in parallel
        const [profileRes, convosRes] = await Promise.all([
          supabase.from('profiles').select('avatar_url').eq('id', user.id).single(),
          supabase.from('conversations').select('id').or(`student_id.eq.${user.id},teacher_id.eq.${user.id}`),
        ]);

        if (profileRes.data?.avatar_url) setAvatarUrl(profileRes.data.avatar_url);

        const convos = convosRes.data;
        if (!convos || convos.length === 0) return;

        const convoIds = convos.map(c => c.id);
        const { count } = await supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .in('conversation_id', convoIds)
          .neq('sender_id', user.id)
          .is('read_at', null);
        setUnreadCount(count || 0);
      } catch (err) {
        console.error('Header data fetch error:', err);
      }
    };
    load();
  }, [user]);

  const { settings: appSettings } = useAppSettings();

  // Filter helpers — apply auth/setting gates from header config
  const isItemVisible = (item: HeaderItem | HeaderCategory & { hide_when_authed?: boolean }): boolean => {
    if (item.auth_required && !user) return false;
    if ('hide_when_authed' in item && item.hide_when_authed && user) return false;
    if (item.requires_setting && !(appSettings as any)[item.requires_setting]) return false;
    return true;
  };

  const itemHref = (item: HeaderItem) =>
    item.target_type === 'page' ? `/p/${item.target_value}` : item.target_value;

  const visibleTopLevel = config.topLevel.filter(isItemVisible);
  const visibleCategories = config.categories
    .filter(isItemVisible)
    .map((c) => ({ ...c, items: c.items.filter(isItemVisible) }))
    .filter((c) => c.items.length > 0);

  const navLinkClass = "text-sm font-medium text-muted-foreground hover:text-foreground transition-colors";

  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="MnemoSpeak" className="h-8 w-auto" />
        </Link>

        {/* Desktop nav — driven by useHeaderConfig */}
        <nav className="hidden items-center gap-5 lg:flex">
          {[...visibleCategories.map((c) => ({ kind: 'cat' as const, sort_order: c.sort_order, data: c })),
            ...visibleTopLevel.map((i) => ({ kind: 'item' as const, sort_order: i.sort_order, data: i }))]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((entry) => entry.kind === 'cat' ? (
              <DropdownMenu key={`cat-${entry.data.id}`}>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {localizedLabel(entry.data, i18n.language)}
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {entry.data.items.map((link) => (
                    <DropdownMenuItem key={link.id} onClick={() => navigate(itemHref(link))}>
                      {localizedLabel(link, i18n.language)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link key={`item-${entry.data.id}`} to={itemHref(entry.data)} className={navLinkClass}>
                {localizedLabel(entry.data, i18n.language)}
              </Link>
            ))}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative rounded-full ring-2 ring-transparent hover:ring-primary/30 transition-all" aria-label={t('common.menu', 'User menu')}>
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={avatarUrl || undefined} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">{initials}</AvatarFallback>
                  </Avatar>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>{t('common.dashboard')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/messages')} className="flex items-center justify-between">
                  {t('nav.messages')}
                  {unreadCount > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/leaderboard')}>{t('nav.leaderboard')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')}>{t('nav.profile')}</DropdownMenuItem>
                {isStaff && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')}>{t('nav.adminPanel')}</DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut().then(() => navigate('/'))}>
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                {t('common.login')}
              </Button>
              <Button size="sm" onClick={() => navigate('/register')}>
                {t('common.register')}
              </Button>
            </div>
          )}
        </nav>

        {/* Mobile toggle */}
        <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'}>
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="border-t bg-background p-4 lg:hidden animate-fade-in">
          <nav className="flex flex-col gap-3">
            {/* Top-level items first (sorted), then category dropdowns as collapsibles */}
            {visibleTopLevel.map((item) => (
              <Link key={item.id} to={itemHref(item)} className="text-sm font-medium" onClick={() => setMobileOpen(false)}>
                {localizedLabel(item, i18n.language)}
              </Link>
            ))}
            {visibleCategories.map((cat) => {
              const open = !!openMobileCats[cat.id];
              return (
                <Collapsible key={cat.id} open={open} onOpenChange={(v) => setOpenMobileCats({ ...openMobileCats, [cat.id]: v })}>
                  <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium w-full">
                    {localizedLabel(cat, i18n.language)}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-3 flex flex-col gap-2 mt-2">
                    {cat.items.map((link) => (
                      <Link key={link.id} to={itemHref(link)} className="text-sm font-medium text-muted-foreground" onClick={() => setMobileOpen(false)}>
                        {localizedLabel(link, i18n.language)}
                      </Link>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
            <div className="border-t my-1" />

            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('common.dashboard')}</Link>
                <Link to="/leaderboard" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('nav.leaderboard')}</Link>
                <Link to="/messages" className="text-sm font-medium flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                  {t('nav.messages')}
                  {unreadCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-destructive-foreground">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
                <Link to="/profile" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('nav.profile')}</Link>
                {isStaff && (
                  <Link to="/admin" className="text-sm font-medium text-primary" onClick={() => setMobileOpen(false)}>{t('nav.adminPanel')}</Link>
                )}
                <div className="border-t pt-2 mt-1">
                  <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => { signOut().then(() => navigate('/')); setMobileOpen(false); }}>
                    {t('common.logout')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => { navigate('/login'); setMobileOpen(false); }}>{t('common.login')}</Button>
                <Button size="sm" onClick={() => { navigate('/register'); setMobileOpen(false); }}>{t('common.register')}</Button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
